import * as React from 'react';
import classNames from 'clsx';
import type { DateRange } from '@wix/editor-elements-corvid-utils';
import {
  DatePickerDate,
  getGridOfDaysInMonth,
  areDatesEqual,
  convertDateRangesArray,
  convertDateStringToDate,
  getDisabledReasons,
} from '@wix/editor-elements-corvid-utils';
import style from '../styles/Month.scss';
import type {
  DayName,
  ICalendarMonthImperativeActions,
  ICalendarMonthProps,
} from '../../DatePickerCalendar.types';

const DayHeaderCell: React.FC<{ dayName: DayName }> = ({ dayName }) => {
  return (
    <th key={dayName.shortName} role="columnheader">
      <span aria-hidden="true">{dayName.shortName}</span>
      <span data-testid="sr-only" className={style.srOnly}>
        {dayName.longName}
      </span>
    </th>
  );
};

export type IDayCellProps = Pick<
  ICalendarMonthProps,
  | 'value'
  | 'allowPastDates'
  | 'allowFutureDates'
  | 'disabledDaysOfWeek'
  | 'year'
  | 'month'
  | 'onDayChange'
  | 'language'
> & {
  day: number | undefined;
  minDate?: Date;
  maxDate?: Date;
  todayDate: Date;
  disabledDates: Array<Date>;
  enabledDateRanges: Array<DateRange<Date>> | null;
  disabledDateRanges: Array<DateRange<Date>>;
  isLoadingMode?: boolean;
  shouldFocus?: boolean;
  focusedDate?: Date;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

const DayCell: React.FC<IDayCellProps> = props => {
  const {
    value,
    minDate,
    maxDate,
    todayDate,
    allowPastDates,
    allowFutureDates,
    disabledDaysOfWeek,
    disabledDates,
    enabledDateRanges,
    disabledDateRanges,
    year,
    month,
    onDayChange,
    day,
    isLoadingMode,
    focusedDate,
    onKeyDown,
    shouldFocus,
    onFocus,
    onBlur,
    language,
  } = props;

  const date = React.useMemo(
    () => new Date(year, month, day),
    [year, month, day],
  );

  const dateTimeFormat = React.useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        month: 'long',
        day: 'numeric',
      }),
    [language],
  );

  const { isDisabledByAnyRule: isDisabled } = React.useMemo(
    () =>
      getDisabledReasons({
        date,
        todayDate,
        minDate,
        maxDate,
        allowPastDates,
        allowFutureDates,
        disabledDaysOfWeek,
        disabledDates,
        enabledDateRanges,
        disabledDateRanges,
      }),
    [
      date,
      todayDate,
      minDate,
      maxDate,
      allowPastDates,
      allowFutureDates,
      disabledDaysOfWeek,
      disabledDates,
      enabledDateRanges,
      disabledDateRanges,
    ],
  );

  const ref = React.useRef<HTMLTableDataCellElement>(null);
  const isFocusedDay = !!focusedDate && focusedDate.getDate() === day;
  React.useEffect(() => {
    if (isFocusedDay && shouldFocus && ref.current) {
      ref.current.focus();
    }
  }, [isFocusedDay, shouldFocus]);

  if (!day) {
    return <td></td>;
  }

  const isSelected = value && areDatesEqual(value, date);
  const isToday = areDatesEqual(date, todayDate);
  const isNonSelectable = isDisabled || isLoadingMode;

  const tdClassNames = classNames({
    [style.disabled]: isDisabled,
    [style.loadingMode]: isLoadingMode,
    [style.selected]: isSelected,
    [style.today]: isToday,
  });

  return (
    <td
      data-testid={isToday ? 'today' : undefined}
      className={tdClassNames}
      aria-selected={isSelected}
      ref={ref}
      tabIndex={isFocusedDay ? 0 : -1}
      onKeyDown={event => {
        if (!isNonSelectable && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onDayChange(day);
        }
        if (isFocusedDay) {
          onKeyDown?.(event);
        }
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={() => {
        if (!isNonSelectable) {
          onDayChange(day);
        }
      }}
      aria-disabled={isNonSelectable}
      aria-label={dateTimeFormat.format(date)}
    >
      <div className={style.day}>
        <span className={style.text}>{day}</span>
      </div>
    </td>
  );
};

const Month: React.ForwardRefRenderFunction<
  ICalendarMonthImperativeActions,
  ICalendarMonthProps
> = (props, ref) => {
  const {
    translations,
    value: valueFromProps,
    minDate,
    maxDate,
    allowPastDates,
    allowFutureDates,
    disabledDates,
    enabledDateRanges,
    disabledDateRanges,
    disabledDaysOfWeek,
    weekStartDay,
    year,
    month,
    onDayChange,
    timeZone,
    isLoadingMode,
    focusedDate,
    onKeyDown,
    language,
  } = props;

  const value = React.useMemo(
    () =>
      typeof valueFromProps === 'string'
        ? new Date(valueFromProps)
        : valueFromProps,
    [valueFromProps],
  );

  const [shouldFocusDay, setShouldFocusDay] = React.useState<boolean>(false);

  const shiftedDayNames = [
    ...translations.dayNames!.slice(weekStartDay),
    ...translations.dayNames!.slice(0, weekStartDay),
  ];

  const dayHeaders = shiftedDayNames.map((dayName, index) => (
    <DayHeaderCell key={index} dayName={dayName}></DayHeaderCell>
  ));

  const memoizedDisabledDates = React.useMemo(
    () => disabledDates.map(date => new Date(date)),
    [disabledDates],
  );

  const todayDate = React.useMemo(
    () =>
      new DatePickerDate({
        type: 'Now',
        timeZone: timeZone || 'Local',
      }).getAsDate('Local'),
    [timeZone],
  );

  const memoizedEnabledDateRanges = React.useMemo(
    () =>
      enabledDateRanges !== null
        ? convertDateRangesArray(enabledDateRanges, convertDateStringToDate)
        : null,
    [enabledDateRanges],
  );

  const memoizedDisabledDateRanges = React.useMemo(
    () => convertDateRangesArray(disabledDateRanges, convertDateStringToDate),
    [disabledDateRanges],
  );

  const minDateAsDate = minDate ? new Date(minDate) : undefined;
  const maxDateAsDate = maxDate ? new Date(maxDate) : undefined;

  const daysGrid = React.useMemo(
    () => getGridOfDaysInMonth(year, month, weekStartDay),
    [year, month, weekStartDay],
  );

  React.useImperativeHandle(ref, () => ({
    focus: () => setShouldFocusDay(true),
    blur: () => setShouldFocusDay(false),
  }));

  const days = daysGrid.map((week, weekIndex) => (
    <tr key={weekIndex}>
      {week.map((_day, index) => (
        <DayCell
          focusedDate={focusedDate}
          onKeyDown={onKeyDown}
          shouldFocus={shouldFocusDay}
          onFocus={() => setShouldFocusDay(true)}
          onBlur={() => setShouldFocusDay(false)}
          key={index}
          year={year}
          month={month}
          day={_day}
          value={value}
          allowPastDates={allowPastDates}
          allowFutureDates={allowFutureDates}
          disabledDaysOfWeek={disabledDaysOfWeek}
          onDayChange={onDayChange}
          minDate={minDateAsDate}
          maxDate={maxDateAsDate}
          todayDate={todayDate}
          disabledDates={memoizedDisabledDates}
          enabledDateRanges={memoizedEnabledDateRanges}
          disabledDateRanges={memoizedDisabledDateRanges}
          isLoadingMode={isLoadingMode}
          language={language}
        ></DayCell>
      ))}
    </tr>
  ));

  return (
    <table data-testid="month" role="table" className={style.month}>
      <thead>
        <tr>{dayHeaders}</tr>
      </thead>
      <tbody>{days}</tbody>
    </table>
  );
};

export default React.forwardRef(Month);
